<!--
    One line of Milo's answer: styled runs, each made of linkify's segments.

    The drawing rules are AssistantText.vue's, moved here so that a paragraph, a
    heading and a list item draw a line the same way. Nothing here is HTML the model
    wrote: a run is bold because the parser said so, a link is a link because linkify
    said so, and the text between is text.
-->
<template>
    <template v-for="(run, r) in runs" :key="r">
        <component :is="tagOf(run.style)" :class="classOf(run.style)">
            <template v-for="(segment, index) in resolved(run.segments)" :key="index">
                <ULink
                    v-if="segment.kind === 'internal'"
                    :to="segment.url"
                    class="font-medium text-primary underline underline-offset-2"
                >{{ segment.text }}</ULink>

                <ULink
                    v-else-if="segment.kind === 'mail'"
                    external
                    :to="segment.url"
                    class="font-medium text-primary underline underline-offset-2 break-words"
                >{{ segment.text }}</ULink>

                <span
                    v-else-if="segment.kind === 'external'"
                    class="group inline-flex max-w-full items-baseline gap-1 align-baseline"
                >
                    <ULink
                        external
                        target="_blank"
                        rel="noopener"
                        :to="segment.url"
                        :title="segment.url"
                        class="font-medium text-primary underline underline-offset-2 break-words"
                    >{{ segment.text }}</ULink>

                    <UButton
                        icon="i-lucide-copy"
                        color="neutral"
                        variant="ghost"
                        size="xs"
                        class="opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 pointer-coarse:opacity-100"
                        :aria-label="$t('assistant.copy_link')"
                        :title="$t('assistant.copy_link')"
                        @click="emit('copy', segment.url)"
                    />
                </span>

                <template v-else>{{ segment.text }}</template>
            </template>
        </component>
    </template>
</template>

<script setup lang="ts">
    import type { ILinkSegment } from '@/libs/linkify'
    import type { IInlineRun, InlineKind } from '@/libs/markdown'

    const props = defineProps<{
        runs: IInlineRun[]
        /** Whether this portal has the screen an internal link names. */
        resolves: (path: string) => boolean
    }>()

    const emit = defineEmits<(e: 'copy', url: string) => void>()

    const tagOf = (style: InlineKind | ''): string => (style === 'bold' ? 'strong' : style === 'italic' ? 'em' : style === 'code' ? 'code' : 'span')

    const classOf = (style: InlineKind | ''): string => (style === 'code' ? 'rounded bg-elevated px-1 font-mono text-[0.9em]' : '')

    /** An internal link to a screen this portal does not have is drawn as text. */
    const resolved = (segments: ILinkSegment[]): ILinkSegment[] => segments.map(segment =>
        segment.kind === 'internal' && !props.resolves(segment.url)
            ? { kind: 'text', text: segment.text, url: '' }
            : segment)
</script>
